// A new network list (D81): a name, what it is for, and optionally its first domains, checked
// here with the server's rules so a typo is caught before it is sent.
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Button, Input, Textarea } from "rizzui";

import { useT } from "../i18n";
import { toast } from "../layout/toast";
import { SideDrawer } from "../ui/SideDrawer";
import { ErrorState } from "../ui/states";
import { createList } from "./api";
import { parseDomains } from "./domains";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Domains to start with (a group's blocked ones). */
  initialDomains?: string[];
  initialName?: string;
}

export function CreateListDrawer({ open, onClose, initialDomains = [], initialName = "" }: Props) {
  const t = useT().network.create;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState("");
  const [domains, setDomains] = useState(initialDomains.join("\n"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const parsed = parseDomains(domains);

  async function create() {
    setBusy(true);
    setError(undefined);
    try {
      const list = await createList({ name: name.trim(), description: description.trim(), domains: parsed.valid });
      await queryClient.invalidateQueries({ queryKey: ["network-lists"] });
      toast(t.created(list.name));
      onClose();
      void navigate(`/network/${list.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      title={t.title}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t.cancel}
          </Button>
          <Button isLoading={busy} disabled={name.trim() === "" || parsed.invalid.length > 0} onClick={() => void create()}>
            {t.create}
          </Button>
        </>
      }
    >
      <Input label={t.name} placeholder={t.namePlaceholder} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} />
      <Textarea label={t.description} placeholder={t.descriptionPlaceholder} value={description} rows={2} onChange={(e) => setDescription(e.target.value)} />
      <Textarea
        label={t.domains}
        placeholder="github.com&#10;.pypi.org"
        value={domains}
        rows={8}
        textareaClassName="font-mono text-[13px]"
        helperText={t.domainsHint}
        error={parsed.invalid.length > 0 ? t.invalid(parsed.invalid.slice(0, 5).join(", ")) : undefined}
        onChange={(e) => setDomains(e.target.value)}
      />
      {error ? <ErrorState message={error} /> : null}
    </SideDrawer>
  );
}
